import { z } from "zod";
import { dataWithRolesSchema } from "./data-with-roles.schemas.js";
import { EventRoleSchema } from "../constants/event-role.constants.js";
import { EventParticipantSchema } from "./event-participant.schemas.js";
import { ParticipantPhotoUrlSchema } from "./participant-photo-url.schemas.js";
import { EventParticipantStatusSchema } from "./participant-state.schemas.js";
import { statusHistoryEntrySchema } from "./status-history.schemas.js";

const _participantEntity = EventParticipantSchema.unwrap();

// Canonical view entity: the full participant with the provenance-bearing
// user-data items flattened to `{ value }` (each derived from the source field
// via `.pick` so the inner value types stay single-sourced and cannot drift —
// `photoUrl` widens only its value brand, because the client resolves the stored
// value to an `/api/...` path the entity's `UrlString` rejects),
// minus backend-only `assumptions` (never read by any client). The role-tier
// views below pick/extend additively off this — each tier supersedes the prior
// and adds host-management fields, mirroring the events view chain.
//
// Fields read by no client are trimmed here so they reach no tier: the
// secondary/assistant contact arrays + related-LinkedIn/company URLs (surfaced
// only on the import response, a separate schema). `createdAt` and
// `statusHistory` are host-only, so they are re-added on the owner tier alone.
const _participantViewBase = _participantEntity
  .omit({
    assumptions: true,
    secondaryEmails: true,
    secondaryPhones: true,
    assistantEmails: true,
    assistantPhones: true,
    relatedLinkedInUrls: true,
    companyUrl: true,
    linkedInCompanyUrl: true,
    createdAt: true,
    statusHistory: true,
    firstName: true,
    lastName: true,
    fullName: true,
    nickName: true,
    title: true,
    bio: true,
    summary: true,
    photoUrl: true,
  })
  .extend({
    firstName: _participantEntity.shape.firstName.pick({ value: true }),
    lastName: _participantEntity.shape.lastName.pick({ value: true }),
    fullName: _participantEntity.shape.fullName.pick({ value: true }),
    nickName: _participantEntity.shape.nickName.pick({ value: true }),
    title: _participantEntity.shape.title
      .unwrap()
      .pick({ value: true })
      .nullable(),
    bio: _participantEntity.shape.bio.unwrap().pick({ value: true }).nullable(),
    summary: _participantEntity.shape.summary
      .unwrap()
      .pick({ value: true })
      .nullable(),
    photoUrl: _participantEntity.shape.photoUrl
      .unwrap()
      .pick({ value: true })
      .extend({ value: ParticipantPhotoUrlSchema })
      .nullable(),
  });

// Host-management fields the participant never sees on their own record.
const OWNER_ONLY_FIELDS = {
  invalidFields: true,
  profile: true,
  vip: true,
} as const;

// Self tier: a participant's own record, without host-management fields.
const _eventParticipantSelfViewSchema =
  _participantViewBase.omit(OWNER_ONLY_FIELDS);

// Owner history hides per-transition metadata; no client reads it.
const _ownerStatusHistoryEntrySchema = statusHistoryEntrySchema(
  EventParticipantStatusSchema,
).omit({ metadata: true });

// Owner tier: the self view plus the host-management fields.
const _eventParticipantOwnerViewSchema = _eventParticipantSelfViewSchema.extend(
  {
    ..._participantViewBase.pick(OWNER_ONLY_FIELDS).shape,
    createdAt: _participantEntity.shape.createdAt,
    statusHistory: z.array(_ownerStatusHistoryEntrySchema).readonly(),
  },
);

export const EventParticipantSelfViewSchema = dataWithRolesSchema(
  _eventParticipantSelfViewSchema.readonly(),
  EventRoleSchema,
);
export type EventParticipantSelfView = z.infer<
  typeof EventParticipantSelfViewSchema
>;

export const EventParticipantOwnerViewSchema = dataWithRolesSchema(
  _eventParticipantOwnerViewSchema.readonly(),
  EventRoleSchema,
);
export type EventParticipantOwnerView = z.infer<
  typeof EventParticipantOwnerViewSchema
>;

// Participant tier: the directory card a participant sees of ANOTHER
// participant. Omission-only off the base — names/title/bio/summary/photoUrl
// keep their `{ value }` projection; host-management (`profile`) and email/phone
// contact fields are dropped (LinkedIn/company are kept). Computed values (the
// display-name cascade, AI-generation flags) are NOT part of the view — they ride
// alongside it in the `DirectoryEntry` envelope. Unlike the Self/Owner tiers this
// view is intentionally not `$roles`-stamped: peers have no roles on others' cards.
const _eventParticipantParticipantViewSchema = _participantViewBase.pick({
  idPublic: true,
  eventIdPublic: true,
  userIdPublic: true,
  firstName: true,
  lastName: true,
  fullName: true,
  nickName: true,
  title: true,
  bio: true,
  summary: true,
  photoUrl: true,
  personalLinkedInUrl: true,
  companyName: true,
});

export const EventParticipantParticipantViewSchema =
  _eventParticipantParticipantViewSchema.readonly();
// Role-tier projection of a participant: owners viewing by id get the
// host-stamped OwnerView; the self path (`/me`, self-by-id, self update/photo)
// gets the SelfView.
export type EventParticipantViewUnion =
  | EventParticipantOwnerView
  | EventParticipantSelfView;
